import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * The Variable Bar — the load-bearing token store.
 * Fill a prospect's details once and every script, SMS, objection line and
 * calculator across the app resolves {{tokens}} against it. Persisted locally.
 */
export type Vars = {
  ownerName: string;
  shopName: string;
  website: string;
  city: string;
  partnerName: string;
  company: string;  // the agency's own name (for "{{company}}")
  repName: string;  // the signed-in setter/closer's name (for "{{repName}}")
  revMo: number | null; // dollars/month
  avgTicket: number | null; // dollars
  reviews: number | null;
  missedPerDay: number; // for the missed-call math
  daysWeek: number;
  apptRatioPct: number; // % of missed inquiries that book an appointment
  closeRatePct: number; // % of appointments that become a job
  projectionMonths: number; // missed-call math window (shared so pop-out stays in sync)
  // Map-pack forecast inputs, shared so Live Call, both calculator tabs and the
  // pop-out stay in sync. Bookings use apptRatioPct × closeRatePct above.
  capacityJobs: number | null; // extra jobs a month he can take on (null = not asked yet)
  callsPerClickPct: number;    // listing clicks that are calls (his GBP: calls ÷ interactions)
  // Fields entered on this call (Live Call answers, calculator edits, the
  // account record), so a screen can tell his numbers from our defaults.
  asked: (keyof Vars)[];
};

export const DEFAULT_VARS: Vars = {
  ownerName: "",
  shopName: "",
  website: "",
  city: "",
  partnerName: "",
  company: "",
  repName: "",
  revMo: null,
  avgTicket: 4200,
  reviews: null,
  missedPerDay: 3,
  daysWeek: 5.5,
  apptRatioPct: 90,
  closeRatePct: 75,
  projectionMonths: 3,
  capacityJobs: null,
  callsPerClickPct: 30,
  asked: [],
};

// Per-call fields are cleared on "New Call"; standing fields persist.
const PER_CALL_KEYS: (keyof Vars)[] = [
  "ownerName",
  "shopName",
  "website",
  "city",
  "partnerName",
  "revMo",
  "reviews",
  "capacityJobs",
  // His numbers: the last prospect's answers mustn't carry into the next call.
  "avgTicket",
  "missedPerDay",
  "apptRatioPct",
  "closeRatePct",
  "callsPerClickPct",
  "asked",
];

const markAsked = (s: Vars, keys: (keyof Vars)[]): (keyof Vars)[] =>
  [...new Set([...(s.asked ?? []), ...keys.filter((k) => k !== "asked")])];

type Ctx = {
  vars: Vars;
  setVar: <K extends keyof Vars>(key: K, value: Vars[K]) => void;
  setMany: (patch: Partial<Vars>) => void;
  resetCall: () => void;
  // A different prospect's account opened: per-call numbers go back to the
  // defaults, then the record's own values fill in (and count as his).
  startAccount: (fromRecord: Partial<Vars>) => void;
  resolve: (text: string) => string;
  tokensPresent: (text: string) => string[];
};

const VarsContext = createContext<Ctx | null>(null);
const KEY = "flowio-vars";

function tokenMap(v: Vars): Record<string, string> {
  const t = (n: number | null, prefix = "") =>
    n == null ? "" : `${prefix}${n.toLocaleString("en-US")}`;
  return {
    ownerName: v.ownerName || "",
    name: v.ownerName || "",
    shopName: v.shopName || "",
    website: v.website || "",
    city: v.city || "",
    partnerName: v.partnerName || "",
    company: v.company || "",
    repName: v.repName || "",
    revMo: t(v.revMo, "$"),
    avgTicket: t(v.avgTicket, "$"),
    ticket: t(v.avgTicket, "$"),
    reviews: t(v.reviews),
    missedPerDay: String(v.missedPerDay),
    daysWeek: String(v.daysWeek),
    apptRatio: `${v.apptRatioPct}%`,
    closeRate: `${v.closeRatePct}%`,
    capturePct: `${Math.round((v.apptRatioPct * v.closeRatePct) / 100)}%`,
  };
}

export function VarsProvider({ children }: { children: ReactNode }) {
  const [vars, setVars] = useState<Vars>(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) return { ...DEFAULT_VARS, ...JSON.parse(raw) };
    } catch {
      /* ignore */
    }
    return DEFAULT_VARS;
  });

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(vars));
  }, [vars]);

  // Live-sync across windows/tabs (fires only for changes made in OTHER windows),
  // so a popped-out presentation view reflects edits from the main window instantly.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== KEY || !e.newValue) return;
      try {
        setVars({ ...DEFAULT_VARS, ...JSON.parse(e.newValue) });
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo<Ctx>(() => {
    const map = tokenMap(vars);
    return {
      vars,
      setVar: (key, val) => setVars((s) => ({ ...s, [key]: val, asked: markAsked(s, [key]) })),
      setMany: (patch) => setVars((s) => ({ ...s, ...patch, asked: markAsked(s, Object.keys(patch) as (keyof Vars)[]) })),
      resetCall: () =>
        setVars((s) => {
          const next = { ...s };
          for (const k of PER_CALL_KEYS) {
            (next[k] as unknown) = DEFAULT_VARS[k];
          }
          // A new call re-opens priming: the next account-aware page fills
          // the tokens fresh from the record (see useAccountVars).
          try { localStorage.removeItem("flowio-vars-primed-account"); } catch { /* ignore */ }
          return next;
        }),
      startAccount: (fromRecord) =>
        setVars((s) => {
          const next = { ...s };
          for (const k of PER_CALL_KEYS) (next[k] as unknown) = DEFAULT_VARS[k];
          const known = (Object.keys(fromRecord) as (keyof Vars)[]).filter((k) => fromRecord[k] != null && fromRecord[k] !== "");
          return { ...next, ...fromRecord, asked: known };
        }),
      resolve: (text: string) =>
        text.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) =>
          map[key] != null && map[key] !== "" ? map[key] : `{{${key}}}`
        ),
      tokensPresent: (text: string) => {
        const found = [...text.matchAll(/\{\{\s*(\w+)\s*\}\}/g)].map((m) => m[1]);
        return [...new Set(found)];
      },
    };
  }, [vars]);

  return <VarsContext.Provider value={value}>{children}</VarsContext.Provider>;
}

export function useVars(): Ctx {
  const ctx = useContext(VarsContext);
  if (!ctx) throw new Error("useVars must be used within VarsProvider");
  return ctx;
}
